import React, { useRef, useState, useEffect } from 'react';
import { Award, Star, Zap, Brain } from 'lucide-react';

const AwardsSection = () => {
  const [hoveredCard, setHoveredCard] = useState(null);
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      {
        threshold: 0.2
      }
    );
  
    // 保存当前的 ref 值
    const currentRef = sectionRef.current;
  
    if (currentRef) {
      observer.observe(currentRef);
    }
  
    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []);

  const awards = [
    {
      icon: <Brain className="w-8 h-8" />,
      title: "Project Management Professional (PMP)",
      date: "Aug 2025 - Aug 2028",
      details: "PMI's global certification in project management methodologies",
      credential: "Credential ID: 4173812"
    },
    {
      icon: <Brain className="w-8 h-8" />,
      title: "Australian Mensa Member",
      date: "Mar 2024 - Mar 2025",
      details: "Top 2% in intelligence scoring worldwide",
      credential: "Credential ID: 11657"
    },
    {
      icon: <Award className="w-8 h-8" />,
      title: "Gold Award - Best Careers Website",
      date: "May 2018",
      details: "Lead developer for Evonik Careers website",
      credential: null
    },
    {
      icon: <Zap className="w-8 h-8" />,
      title: "Google Analytics Individual Qualification",
      date: "2019",
      details: "Advanced proficiency in analytics and tracking",
      credential: "Credential ID: 26644296"
    }
  ];

  return (
    <section 
      ref={sectionRef} 
      className="relative min-h-screen bg-gradient-to-br from-white to-sky-50 py-24 overflow-hidden"
    >
      {/* Background Grid */}
      <div className="absolute inset-0" 
        style={{
          backgroundImage: 'linear-gradient(to right, #f1f5f9 1px, transparent 1px), linear-gradient(to bottom, #f1f5f9 1px, transparent 1px)',
          backgroundSize: '4rem 4rem'
        }}
      />

      {/* Content Container */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className={`text-center mb-16 transform transition-all duration-1000 ${
          isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
        }`}>
          <h2 className="text-4xl font-light text-slate-700 tracking-wide mb-4">
            Awards & Certifications
          </h2>
          <div className="w-24 h-0.5 bg-gradient-to-r from-sky-400 to-sky-600 mx-auto"></div>
        </div>

        {/* Awards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-8">
          {awards.map((award, index) => (
            <div
              key={index}
              className={`transform transition-all duration-1000 ${
                isVisible 
                  ? 'translate-y-0 opacity-100' 
                  : 'translate-y-16 opacity-0'
              }`}
              style={{ transitionDelay: `${index * 200}ms` }}
            >
              <div
                className={`group relative h-full p-8 rounded-xl backdrop-blur-sm
                  bg-gradient-to-br from-white/70 to-white/50
                  border border-white/20 shadow-lg hover:shadow-xl
                  transition-all duration-300 hover:-translate-y-1
                  ${hoveredCard === index ? 'scale-[1.02]' : 'scale-100'}`}
                onMouseEnter={() => setHoveredCard(index)}
                onMouseLeave={() => setHoveredCard(null)}
              >
                {/* Icon with gradient background */}
                <div className="relative mb-6">
                  <div className="absolute inset-0 bg-gradient-to-br from-sky-100 to-white rounded-full transform -rotate-6 transition-transform group-hover:rotate-3"></div>
                  <div className="relative p-4 text-sky-600 transform transition-transform group-hover:scale-110">
                    {award.icon}
                  </div>
                </div>

                {/* Content */}
                <h3 className="text-xl font-medium text-slate-700 mb-2 group-hover:text-sky-600 transition-colors">
                  {award.title}
                </h3>
                <p className="text-slate-500 mb-4">
                  {award.date}
                </p>
                <p className="text-slate-600 leading-relaxed">
                  {award.details}
                </p>
                {award.credential && (
                  <p className="text-sm text-slate-500 mt-4 font-mono">
                    {award.credential}
                  </p>
                )}

                {/* Decorative elements */}
                <div className="absolute top-0 right-0 p-4 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Star className="w-5 h-5 text-sky-400" />
                </div>
                <div className="absolute bottom-0 right-0 w-24 h-24 bg-gradient-to-br from-sky-100/20 to-transparent rounded-full transform translate-x-8 translate-y-8 group-hover:translate-x-6 group-hover:translate-y-6 transition-transform"></div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Decorative background elements */}
      <div className="absolute -bottom-32 right-0 w-96 h-96 bg-gradient-to-br from-sky-100/40 to-slate-100/40 rounded-full mix-blend-multiply filter blur-3xl opacity-70 animate-blob" />
      <div className="absolute top-32 -left-32 w-96 h-96 bg-gradient-to-br from-slate-100/40 to-sky-100/40 rounded-full mix-blend-multiply filter blur-3xl opacity-70 animate-blob animation-delay-2000" />
    </section>
  );
};

export default AwardsSection;